import { AccordionActions } from "@mui/material";
import { Accordion } from "@trackman/web-shared-components";
import { AppButton } from "components/form";
import { AppSpacer, AppUseIcon } from "components/ui";
import { AppLoader } from "components/ui/AppLoader";
import { t } from "i18next";
import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IUserDiagnostic, IUserDiagnosticsDownloadFileRequestDto, IUserDiagnosticsFileEntry, UserDiagnosticsFileType } from "types/models/UserDiagnosticsModel";
import { isImageFile } from "utilities/FilePathUtils";

interface IUserDignosticsFilesTabProps {
    isDownloadingUserDiagnosticsFile: boolean;
    userDiagnostic: IUserDiagnostic | null
}

export const UserDignosticsFilesTab = observer(({ isDownloadingUserDiagnosticsFile, userDiagnostic }: IUserDignosticsFilesTabProps) => {
    const { userDiagnosticStore } = useStores();
    const [downloadingElement, setDownloadingElement] = useState<string | null>(null);
    const [groupedFileTypeExpanded, setGroupedFileTypeExpanded] = useState<boolean[]>([]);
    const [groupedByFileType, setGroupedByFileType] = useState<Record<UserDiagnosticsFileType, IUserDiagnosticsFileEntry[]> | null>(null);

    const fileEntries = useMemo(() => {
        return userDiagnostic?.fileEntries || null;
    }, [userDiagnostic]);

    if (!fileEntries) {
        return <div className='container'>
            <h3 className='font-22 font-500 text-uppercase mb-0 text-uppercase font-22 font-500'>{t('UserDiagnostics.Tab.files.No.Files') as string}</h3>
        </div>
    }

    const renderFileEntryContent = (entry: IUserDiagnosticsFileEntry) => {
        const classStyling = 'font-10 font-700 font-lato text-capitalize color-secondaryGrey mb-0'
        if (entry.fileType === 0) {
            if (isImageFile(entry.relativeFilePath)) {
                return <p onClick={() => OnOpenPreview(entry.relativeFilePath)} className={classStyling} style={{ cursor: 'pointer' }}>
                    {entry.relativeFilePath}
                </p>
            }
        }

        return <p className={classStyling}> {entry.relativeFilePath} </p>
    };

    useEffect(() => {
        if (!fileEntries) {
            setGroupedByFileType(null);
            setGroupedFileTypeExpanded([]);
            return;
        }

        const grouped = fileEntries.reduce((acc, entry) => {
            const key = entry.fileType;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(entry);
            return acc;
        }, {} as Record<UserDiagnosticsFileType, IUserDiagnosticsFileEntry[]>);

        Object.keys(grouped).forEach((key) => {
            grouped[Number(key)] = grouped[Number(key)].sort((a, b) =>
                a.relativeFilePath.localeCompare(b.relativeFilePath)
            );
        });

        setGroupedByFileType(grouped);
        setGroupedFileTypeExpanded(Object.keys(grouped).map(() => false));
    }, [fileEntries]);

    useEffect(() => {
        if (!isDownloadingUserDiagnosticsFile && downloadingElement !== null) {
            setDownloadingElement(null);
        }
    }, [isDownloadingUserDiagnosticsFile]);

    const OnOpenPreview = useCallback((fileName: string) => {
        if (userDiagnostic === null) {
            return;
        }
        userDiagnosticStore.setSelectedFileEntry({
            userDiagnosticsId: userDiagnostic?.id,
            fileName: fileName,
            blob: null
        })
    }, [userDiagnostic]);

    const OnDownload = useCallback((key: string, requestConfig: IUserDiagnosticsDownloadFileRequestDto) => {
        if (userDiagnostic === null) {
            return;
        }
        setDownloadingElement(key)

        userDiagnosticStore.downloadUserDiagnosticsFile(userDiagnostic?.id ?? null, requestConfig)
    }, [userDiagnostic]);

    return (
        <>
            {groupedByFileType && Object.entries(groupedByFileType).map(([fileType, entries], index) => (
                <Accordion
                    key={index}
                    expanded={groupedFileTypeExpanded[index]}
                    paperProps={{ className: 'mb-3' }}
                    severity='none'
                    onChange={(e) => {
                        const newState = [...groupedFileTypeExpanded];
                        newState[index] = !newState[index];
                        setGroupedFileTypeExpanded(newState);
                    }}
                    title={`${UserDiagnosticsFileType[Number(fileType)]} - ${entries.length}`}
                >
                    <ul>
                        {entries.map((entry, index) => (
                            <li key={index} className='d-flex align-items-center w-100 mb-4'>
                                {renderFileEntryContent(entry)}
                                <AppSpacer isHorizontal space='1rem' />
                                <AppLoader isShowingLoader={isDownloadingUserDiagnosticsFile && downloadingElement === `entry-${entry.relativeFilePath}`} loaderSize="small">
                                    <button
                                        className={`btn btn-transparent p-0 d-flex align-items-center justify-content-center ml-2`}
                                        disabled={isDownloadingUserDiagnosticsFile || !userDiagnostic?.hasDiagnosticsPackage}
                                        onClick={() => OnDownload(`entry-${entry.relativeFilePath}`, {
                                            specificFileUrl: entry.relativeFilePath
                                        })}>
                                        <AppUseIcon name='DownloadIcon' width='24' height='24' />
                                    </button>
                                </AppLoader>
                            </li>
                        ))}
                    </ul>
                    <AccordionActions>
                        <AppLoader isShowingLoader={isDownloadingUserDiagnosticsFile && downloadingElement === `entry-${fileType}`}>
                            <AppButton
                                variant='primary'
                                className='border-primary'
                                disabled={isDownloadingUserDiagnosticsFile || !userDiagnostic?.hasDiagnosticsPackage}
                                onClick={() => OnDownload(`entry-${fileType}`, {
                                    specificFileType: UserDiagnosticsFileType[fileType as keyof typeof UserDiagnosticsFileType]
                                })}
                            >
                                {t('UserDiagnostics.Action.DownloadAll').toString()}
                            </AppButton>
                        </AppLoader>
                    </AccordionActions>
                </Accordion>
            ))}
        </>
    );
})